// AdminLanguageSwitcher — Phase 10 follow-up (2026-05-12).
//
// Visually + behaviourally IDENTICAL to the player-facing
// shared-ui/src/components/LanguageSwitcher. The only differences:
//
//   * Auth source is `useAdminAuth()` (agent role) instead of the
//     player `useAuth()`, so the dropdown reads the active language
//     off the agent's row and refreshes the AdminAuthProvider after
//     a successful pick.
//
//   * Persistence endpoint is `PATCH /admin/me-agent/language` (via
//     `adminApi.patch`) instead of `PATCH /me`. The body shape is
//     identical: `{ language: <code> }`.
//
// The JSX, sizing, padding, colours, z-layering, accessibility
// behaviour, keyboard handling, sticky-header layout, and 2-column
// native-script card grid are intentionally a verbatim copy from
// the player file — the user's directive was "do not alter the
// premium styling". Any future styling change MUST be applied to
// both files in lock-step. The two files cross-reference each other
// in their respective top-of-file headers so a future maintainer
// finds the sibling immediately.
//
// Mount point: AgentNavBar, between the SseAlertsBell and the
// AdminThemeToggle. The trigger sits in the right utility cluster,
// keeping the player + agent navbars feeling like the same surface.

import { useEffect, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { SUPPORTED_LANGS } from '../../lib/translations';
import type { Lang } from '../../lib/translations';
import { useAdminAuth } from '../lib/adminAuth';
import { adminApi } from '../lib/adminApi';
import { useToast } from '../../lib/toast';
import Icon from '../../components/Icon';

export default function AdminLanguageSwitcher() {
  const { lang, setLang, t } = useI18n();
  const { admin, refresh } = useAdminAuth();
  const { push } = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<Lang | null>(null);

  // Close on Escape so the modal honours OS-standard dismissal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  async function pick(code: Lang) {
    if (code === lang) { setOpen(false); return; }
    // Optimistic UI: switch the language immediately so the agent
    // sees the change land in <250ms. If the backend write fails
    // (only relevant when an agent is logged in) we still keep the
    // local choice — the picker is not destructive and the next
    // /admin/auth/me read will reconcile.
    setBusy(code);
    setLang(code);
    // Persist server-side only when an agent (not platform admin) is
    // signed in. The admin role doesn't carry an `agents.language`
    // row; the player end logic of "no user → localStorage only" is
    // preserved exactly.
    if (admin?.role === 'agent') {
      try {
        await adminApi.patch('/admin/me-agent/language', { language: code });
        await refresh();
      } catch (e) {
        push('error', (e as Error).message || 'Could not save language');
      }
    }
    setBusy(null);
    setOpen(false);
  }

  const current = SUPPORTED_LANGS.find((l) => l.code === lang) ?? SUPPORTED_LANGS[0];

  return (
    <>
      {/* Trigger — small circular pill with a globe icon and the
          current language code beside it. Globe means "languages" even
          to an agent who can't read; the 2-letter code tells them
          what's set. Sits next to AdminThemeToggle in AgentNavBar. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('lang.switcher.aria')}
        title={t('lang.switcher.aria')}
        className="inline-flex items-center gap-1 h-10 pl-2 pr-2.5 rounded-full
                   border border-arena-200 dark:border-neon-400/30
                   bg-white/70 dark:bg-arena-800/70
                   text-arena-700 dark:text-neon-300
                   font-bold text-[11px] uppercase tracking-wider
                   active:scale-95 transition"
      >
        <Icon name="language" size={20} filled />
        <span className="tabular-nums">{current.code.toUpperCase()}</span>
      </button>

      {/* Dropdown — anchored to the top-right of the viewport, just
          below the NavBar's bottom edge. Hard top/bottom positioning
          using env(safe-area-inset-*) means it can't drift above the
          fold on phones with notches, and overflow-y:auto on the inner
          panel lets the 12 cards scroll internally if the device is
          short — never half-hidden, always tappable.

          Layered as:
            [60] click-catcher backdrop (closes on outside tap)
            [61] panel itself

          Note: AgentNavBar lives at z-[10000] (above warning + lockout
          modals). z-60/61 here is RELATIVE to the navbar's normal
          stacking context — the dropdown renders on top of the
          navbar's siblings (page content + modals) just fine because
          the navbar's z-10000 covers all of it. Keeping z-60/61 in
          lock-step with the player file means future styling tweaks
          stay one-to-one. */}
      {open && (
        <>
          {/* Invisible full-viewport backdrop — captures clicks outside
              the panel to close it. Slight tint so the page recedes. */}
          <div
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-[1px]"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          {/* Panel — anchored top-right, scrolls internally */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('lang.switcher.title')}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[61] right-2 sm:right-4
                       w-[min(20rem,calc(100vw-1rem))]
                       rounded-2xl
                       bg-white dark:bg-arena-800
                       border border-arena-200 dark:border-neon-400/25
                       shadow-2xl overflow-hidden
                       animate-slide-down"
            style={{
              // Sit just below the NavBar (h-12 = 3rem) + the iOS safe
              // area at the top. 0.5rem of breathing room.
              top: 'calc(3rem + env(safe-area-inset-top) + 0.5rem)',
              // Cap height to exactly what fits between the navbar and
              // the bottom safe-area; the inner list scrolls if needed.
              maxHeight:
                'calc(100dvh - 3rem - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 1rem)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header — sticky so it's always visible while scrolling */}
            <div className="px-4 pt-3 pb-2 flex items-start gap-3 border-b border-arena-200 dark:border-arena-600/40 shrink-0">
              <div className="w-9 h-9 rounded-xl grid place-items-center
                              bg-gradient-to-br from-neon-300/40 to-brand-300/30
                              dark:from-neon-400/20 dark:to-brand-500/20
                              text-arena-700 dark:text-neon-300 shrink-0">
                <Icon name="language" size={20} filled />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display font-extrabold text-sm text-arena-800 dark:text-neon-100 leading-tight">
                  {t('lang.switcher.title')}
                </h2>
                <p className="text-[11px] text-arena-500 dark:text-neon-200/70 leading-snug">
                  {t('lang.switcher.sub')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('common.cancel')}
                className="shrink-0 w-8 h-8 -mt-0.5 -mr-1 rounded-full grid place-items-center
                           text-arena-500 dark:text-neon-200/70
                           hover:bg-arena-100/70 dark:hover:bg-arena-700/70 transition"
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            {/* Scrollable list — 2-column grid of native-script cards.
                Active language gets a cyan ring + check tick. */}
            <div className="overflow-y-auto overscroll-contain p-3">
              <div className="grid grid-cols-2 gap-2">
                {SUPPORTED_LANGS.map((l) => {
                  const active = l.code === lang;
                  const isBusy = busy === l.code;
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => !isBusy && pick(l.code)}
                      disabled={busy !== null}
                      aria-pressed={active}
                      dir={l.code === 'ur' ? 'rtl' : 'ltr'}
                      className={`relative text-left p-2.5 rounded-xl border transition active:scale-[.98]
                                  ${active
                                    ? 'border-neon-400 bg-neon-100/70 dark:bg-neon-400/10 shadow-neon-cyan'
                                    : 'border-arena-200 dark:border-arena-600/40 bg-arena-50/60 dark:bg-arena-700/40 hover:border-neon-400/50'}
                                  ${isBusy ? 'opacity-70' : ''}`}
                    >
                      <div className="font-display font-extrabold text-[15px] leading-tight text-arena-800 dark:text-neon-100">
                        {l.native}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider text-arena-500 dark:text-neon-200/60 mt-0.5">
                        {l.english}
                      </div>
                      {active && (
                        <Icon name="check_circle" filled
                              className="absolute top-1.5 right-1.5 text-neon-600 dark:text-neon-300" size={16} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-arena-200 dark:border-arena-600/40 shrink-0">
              <div className="text-[10px] uppercase tracking-[.22em] text-center text-arena-400 dark:text-neon-200/60">
                {t('lang.switcher.current')}: <b className="font-bold text-arena-700 dark:text-neon-200">{current.native}</b>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
